import { isUndefined } from 'lodash-es';

import { isRectangleSelected } from './is-rectangle-selected.js';
import { serializeLinearSeriesValues } from './serialize-linear-series-values.js';
import { serializeRectangleSeriesValues } from './serialize-rectangle-series-values.js';
import { POINT_OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import type { SelectState } from '../../../../core/components/interactions-state-helpers/types.js';
import type { ColorToken } from '../../../../core/types/color-palette.js';
import type { AnyScale } from '../../../../core/types/scales.js';
import { getSelectionPixels } from '../../../../core/utils/get-selection-pixels.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { scaleBandInvert } from '../../../../core/utils/scale.js';
import type { XYChartLinearSelectionContextValue } from '../../../context/XYChartLinearSelection.context.js';
import type { AxisIdToScales } from '../../../context/XYChartScales.context.js';
import type { useBarData } from '../../../hooks/useBarData.js';
import type { useRectData } from '../../../hooks/useRectData.js';
import type { useXYChartRectSelection } from '../../../hooks/useXYChartRectSelection.js';
import type { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import type { useSelectState } from '../../../store/selectors.js';
import type { XYAxisInternalTypes } from '../../../types/axis.js';
import {
  isAnyNumericalScale,
  isCategoricalScale,
  isDotContentPoint,
  isLineContentPoint,
  isAreaContentPoint,
} from '../../../types/guards.js';
import type {
  TooltipContent,
  XYChartExploreState,
} from '../../../types/state.js';
import {
  type AnyLinearMetadata,
  type AnyLinearSeries,
  type AxisIdToScaleDomain,
  BAR_ID,
  BAR_VARIANT,
  type BarSeriesToMetadata,
  type BarSeriesWithDatapoints,
  DOT_ID,
  DOT_VARIANT,
  type DotMetadata,
  type DotSeriesToMetadata,
  type DotSeriesWithDatapoints,
  type HighlightedLinearPoint,
  LINE_ID,
  LINE_VARIANT,
  type LineMetadata,
  type LineSeriesToMetadata,
  type LineSeriesWithDatapoints,
  RECT_ID,
  RECT_VARIANT,
  type RectDatapointInternal,
  type RectMetadata,
  type RectSeriesToMetadata,
  type RectSeriesWithDatapoints,
  AREA_ID,
  type AreaMetadata,
  type AreaSeriesWithDatapoints,
  type MapDatapoints,
  type XYChartAxisInternal,
  type XYChartSeriesWithDatapoints,
  AREA_VARIANT,
  type AreaSeriesToMetadata,
} from '../../../types/xy-chart-internals.js';
import type {
  AreaDatapoint,
  BarDatapoint,
  DotDatapoint,
  LineDatapoint,
} from '../../../types/xy-chart.js';
import { getLinearAccessors } from '../../../utils/get-linear-accessors.js';
import {
  getCurrentScale,
  getDimensionScale,
} from '../../../utils/get-scale.js';
import {
  isAreaSeries,
  isBarSeries,
  isDotSeries,
  isLineSeries,
  isRectSeries,
} from '../../../utils/type-guards.js';
import { isGapDatapointForDisplay } from '../../../utils/validation-datapoint.js';
import {
  relAccessor,
  type BinDomain,
} from '../../../utils/value-representation.js';
import {
  x1Accessor,
  xAccessor,
  y1Accessor,
  yAccessor,
} from '../../displays/area-accessors.js';
import {
  bandX0Accessor,
  bandX1Accessor,
  bandY0Accessor,
  bandY1Accessor,
} from '../../displays/band-accessors.js';
import {
  heightAccessor as barHeightAccessor,
  widthAccessor as barWidthAccessor,
  xAccessor as xBarAccessor,
  yAccessor as yBarAccessor,
} from '../../displays/bar-accessors.js';
import {
  isXBaselineAccessor,
  isYBaselineAccessor,
} from '../../displays/BarDisplay/BarDisplay.js';
import {
  xAccessor as xDotAccessor,
  yAccessor as yDotAccessor,
} from '../../displays/dot-accessors.js';
import {
  xAccessor as xLineAccessor,
  yAccessor as yLineAccessor,
} from '../../displays/line-accessors.js';
import {
  heightAccessor,
  widthAccessor,
  xAccessor as xRectAccessor,
  yAccessor as yRectAccessor,
} from '../../displays/rect-accessors.js';
import type { RectanglePoint } from '../explore/types.js';

type DomainValue = [number, number] | [Date, Date] | string[];

export interface XYChartSelectedDatapointsPerSeries {
  seriesName: string;
  datapoints:
    | DotDatapoint[]
    | LineDatapoint[]
    | RectDatapointInternal[]
    | BarDatapoint[]
    | AreaDatapoint[];
  domain: DomainValue;
}

interface SelectionXAxisConfig {
  axisType: XYAxisInternalTypes | undefined;
  axisScale: AnyScale | undefined;
}

function getCategoriesIncluded(
  categories: string[],
  domainStart: string,
  domainEnd: string,
) {
  const startIndex = categories.indexOf(domainStart);
  const endIndex = categories.indexOf(domainEnd);

  return categories.slice(startIndex, endIndex);
}

function getSelectionDomain(
  selection: { start: number; end: number },
  axisConfig: SelectionXAxisConfig,
): DomainValue {
  if (isAnyNumericalScale(axisConfig)) {
    return [
      axisConfig.axisScale.invert(selection.start),
      axisConfig.axisScale.invert(selection.end),
    ] as [number, number] | [Date, Date];
  } else if (isCategoricalScale(axisConfig)) {
    const domainStart = scaleBandInvert(axisConfig.axisScale)(selection.start);
    const domainEnd = scaleBandInvert(axisConfig.axisScale)(selection.end);
    const categories = axisConfig.axisScale.domain();
    return getCategoriesIncluded(categories, domainStart, domainEnd);
  }

  return [selection.start, selection.end];
}

export const getSelectedDatapoints = (
  seriesWithDatapoints: XYChartSeriesWithDatapoints[],
  axisIdToScales: AxisIdToScales,
  axes: XYChartAxisInternal[],
  selection: { start: number; end: number },
  dotSeriesToMetadata: DotSeriesToMetadata,
  lineSeriesToMetadata: LineSeriesToMetadata,
  rectSeriesToMetadata: RectSeriesToMetadata,
  datapointMetadataMap: MapDatapoints,
  axisIdToScaleDomain: Map<string, BinDomain>,
  barSeriesToMetadata: BarSeriesToMetadata,
  areaSeriesToMetadata: AreaSeriesToMetadata,
) => {
  const rectSeriesIndex = { current: 0 };
  const isDefined = isGapDatapointForDisplay(datapointMetadataMap);

  return seriesWithDatapoints.map((series) => {
    const xScale = getCurrentScale(axisIdToScales, series.xAxisId);
    const yScale = getCurrentScale(axisIdToScales, series.yAxisId);
    const [yMin, yMax] = yScale.range().toSorted((a, b) => a - b);
    const axisConfig = {
      axisType: axes.find((axis) => axis.id === series.xAxisId)?.type,
      axisScale: xScale,
    };

    if (isDotSeries(series)) {
      const { xAxis, yAxis } = safeGet(dotSeriesToMetadata, series);
      const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

      return getDotSeriesSelection(
        series,
        dotSeriesToMetadata,
        xScale,
        yScale,
        selection,
        [yMin, yMax],
        axisConfig,
        xRelAccessor,
        yRelAccessor,
      );
    } else if (isLineSeries(series)) {
      const { xAxis, yAxis } = safeGet(lineSeriesToMetadata, series);
      const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

      return getLineSeriesSelection(
        series,
        lineSeriesToMetadata,
        xScale,
        yScale,
        selection,
        [yMin, yMax],
        axisConfig,
        isDefined,
        xRelAccessor,
        yRelAccessor,
      );
    } else if (isRectSeries(series)) {
      const { xAxis, yAxis } = safeGet(rectSeriesToMetadata, series);
      const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

      return getRectSeriesSelection(series, {
        rectSeriesIndex,
        xScale,
        yScale,
        selection,
        yRange: [yMin, yMax],
        axisConfig,
        isDefined,
        xRelAccessor,
        yRelAccessor,
      });
    } else if (isBarSeries(series)) {
      const { xAxis, yAxis } = safeGet(barSeriesToMetadata, series);
      const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

      return getBarSeriesSelection(
        barSeriesToMetadata,
        series,
        selection,
        [yMin, yMax],
        axisConfig,
        datapointMetadataMap,
        axisIdToScales,
        xRelAccessor,
        yRelAccessor,
      );
    } else if (isAreaSeries(series)) {
      const { xAxis, yAxis } = safeGet(areaSeriesToMetadata, series);
      const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
      const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

      return getAreaSeriesSelection(series, {
        areaSeriesToMetadata,
        xScale,
        yScale,
        selection,
        yRange: [yMin, yMax],
        axisConfig,
        isDefined,
        datapointMetadataMap,
        xRelAccessor,
        yRelAccessor,
      });
    } else {
      return {
        seriesName: '',
        datapoints: [],
        domain: getSelectionDomain(selection, axisConfig),
      };
    }
  });
};

function getDotSeriesSelection(
  series: DotSeriesWithDatapoints,
  dotSeriesToMetadata: DotSeriesToMetadata,
  xScale: AnyScale,
  yScale: AnyScale,
  selection: { start: number; end: number },
  yRange: [number, number],
  axisConfig: SelectionXAxisConfig,
  xRelAccessor?: (value: number) => number,
  yRelAccessor?: (value: number) => number,
): XYChartSelectedDatapointsPerSeries {
  const filteredData = filterPointSeries(
    series.data,
    xDotAccessor(xScale, xRelAccessor),
    yDotAccessor(yScale, yRelAccessor),
    selection,
    yRange,
  );

  return {
    seriesName: safeGet(dotSeriesToMetadata, series).name,
    datapoints: filteredData,
    domain: getSelectionDomain(selection, axisConfig),
  };
}

function getLineSeriesSelection(
  series: LineSeriesWithDatapoints,
  lineSeriesToMetadata: LineSeriesToMetadata,
  xScale: AnyScale,
  yScale: AnyScale,
  selection: { start: number; end: number },
  yRange: [number, number],
  axisConfig: SelectionXAxisConfig,
  isDefined: ReturnType<typeof isGapDatapointForDisplay>,
  xRelAccessor?: (value: number) => number,
  yRelAccessor?: (value: number) => number,
) {
  const filteredData = filterPointSeries(
    series.data,
    xLineAccessor(xScale, xRelAccessor),
    yLineAccessor(yScale, yRelAccessor),
    selection,
    yRange,
    isDefined,
  );

  return {
    seriesName: safeGet(lineSeriesToMetadata, series).name,
    datapoints: filteredData,
    domain: getSelectionDomain(selection, axisConfig),
  };
}

/**
 * Returns true when the element's vertical extent is fully contained within
 * the plot area's y-range. This excludes datapoints whose y-value falls
 * outside the current y-axis domain, while still keeping bars with zero or sub-pixel height that
 * sit on the baseline.
 */
function isWithinVerticalDomain(
  yStart: number,
  yEnd: number,
  yMin: number,
  yMax: number,
): boolean {
  const top = Math.min(yStart, yEnd);
  const bottom = Math.max(yStart, yEnd);
  return top >= yMin && bottom <= yMax;
}

function getRectSeriesSelection(
  series: RectSeriesWithDatapoints,
  ctx: {
    rectSeriesIndex: { current: number };
    xScale: AnyScale;
    yScale: AnyScale;
    selection: { start: number; end: number };
    yRange: [number, number];
    axisConfig: SelectionXAxisConfig;
    isDefined: ReturnType<typeof isGapDatapointForDisplay>;
    xRelAccessor?: (value: number) => number;
    yRelAccessor?: (value: number) => number;
  },
): XYChartSelectedDatapointsPerSeries {
  const {
    rectSeriesIndex,
    xScale,
    yScale,
    selection,
    yRange,
    axisConfig,
    isDefined,
    xRelAccessor,
    yRelAccessor,
  } = ctx;

  const xAccessor = xRectAccessor(xScale, xRelAccessor);
  const yAccessor = yRectAccessor(yScale, yRelAccessor);
  const horizontalBandAccessor = widthAccessor(xScale, xRelAccessor);
  const verticalBandAccessor = heightAccessor(yScale, yRelAccessor);
  const [yMin, yMax] = yRange;

  const filteredDatapoints = series.filteredData.filter((datapoint) => {
    if (!isDefined(datapoint)) {
      return false;
    }

    const rectXStart = xAccessor(datapoint);
    const rectXEnd = rectXStart + horizontalBandAccessor(datapoint);
    const rectYStart = yAccessor(datapoint);
    const rectYEnd = rectYStart + verticalBandAccessor(datapoint);

    if (!isWithinVerticalDomain(rectYStart, rectYEnd, yMin, yMax)) {
      return false;
    }

    return isRectangleSelected({
      x: {
        rectangleStart: rectXStart,
        rectangleEnd: rectXEnd,
        selectionStart: selection.start,
        selectionEnd: selection.end,
      },
      y: {
        rectangleStart: rectYStart,
        rectangleEnd: rectYEnd,
        selectionStart: yMin,
        selectionEnd: yMax,
      },
    });
  });

  return {
    seriesName: `rect-series-${++rectSeriesIndex.current}`,
    datapoints: filteredDatapoints,
    domain: getSelectionDomain(selection, axisConfig),
  };
}

function getBarSeriesSelection(
  barSeriesToMetadata: BarSeriesToMetadata,
  series: BarSeriesWithDatapoints,
  selection: { start: number; end: number },
  yRange: [number, number],
  axisConfig: SelectionXAxisConfig,
  datapointMetadataMap: MapDatapoints,
  axisIdToScales: AxisIdToScales,
  xRelAccessor?: (value: number) => number,
  yRelAccessor?: (value: number) => number,
): XYChartSelectedDatapointsPerSeries {
  const xScale = getCurrentScale(
    axisIdToScales,
    series.xAxisId,
    isXBaselineAccessor(series.data[0]) ? series.type : undefined,
  );
  const yScale = getCurrentScale(
    axisIdToScales,
    series.yAxisId,
    isYBaselineAccessor(series.data[0]) ? series.type : undefined,
  );

  const [yMin, yMax] = yRange;
  const dimensionXScale = getDimensionScale(axisIdToScales, series.xAxisId);
  const dimensionYScale = getDimensionScale(axisIdToScales, series.yAxisId);

  const xAccessor = xBarAccessor(
    xScale,
    datapointMetadataMap,
    dimensionXScale,
    series.id,
    xRelAccessor,
  );
  const yAccessor = yBarAccessor(
    yScale,
    datapointMetadataMap,
    dimensionYScale,
    series.id,
    yRelAccessor,
  );

  const horizontalBandAccessor = barWidthAccessor(
    xScale,
    datapointMetadataMap,
    dimensionXScale,
    xRelAccessor,
  );

  const verticalBandAccessor = barHeightAccessor(
    yScale,
    datapointMetadataMap,
    dimensionYScale,
    yRelAccessor,
  );

  const filteredData = series.data.filter((datapoint) => {
    const barXStart = xAccessor(datapoint);
    const barXEnd = barXStart + horizontalBandAccessor(datapoint);
    const barYStart = yAccessor(datapoint);
    const barYEnd = barYStart + verticalBandAccessor(datapoint);

    if (!isWithinVerticalDomain(barYStart, barYEnd, yMin, yMax)) {
      return false;
    }

    return isRectangleSelected({
      x: {
        rectangleStart: barXStart,
        rectangleEnd: barXEnd,
        selectionStart: selection.start,
        selectionEnd: selection.end,
      },
      y: {
        rectangleStart: barYStart,
        rectangleEnd: barYEnd,
        selectionStart: yMin,
        selectionEnd: yMax,
      },
    });
  });

  return {
    seriesName: safeGet(barSeriesToMetadata, series).name,
    datapoints: filteredData,
    domain: getSelectionDomain(selection, axisConfig),
  };
}

function getAreaSeriesSelection(
  series: AreaSeriesWithDatapoints,
  ctx: {
    areaSeriesToMetadata: Map<AreaSeriesWithDatapoints, AreaMetadata>;
    xScale: AnyScale;
    yScale: AnyScale;
    selection: { start: number; end: number };
    yRange: [number, number];
    axisConfig: SelectionXAxisConfig;
    isDefined: ReturnType<typeof isGapDatapointForDisplay>;
    datapointMetadataMap: MapDatapoints;
    xRelAccessor?: (value: number) => number;
    yRelAccessor?: (value: number) => number;
  },
): XYChartSelectedDatapointsPerSeries {
  const {
    areaSeriesToMetadata,
    xScale,
    yScale,
    selection,
    yRange,
    axisConfig,
    isDefined,
    datapointMetadataMap,
    xRelAccessor,
    yRelAccessor,
  } = ctx;

  const { name } = safeGet(areaSeriesToMetadata, series);

  const [yMin, yMax] = yRange;

  const { xAccessor: xAreaAccessor, yAccessor: yAreaAccessor } =
    getLinearAccessors(
      AREA_VARIANT,
      safeGet(areaSeriesToMetadata, series),
      datapointMetadataMap,
      xRelAccessor,
      yRelAccessor,
    );

  const filteredData = series.data.filter((datapoint) => {
    if (!isDefined(datapoint)) {
      return false;
    }

    const xCoord = xAreaAccessor(xScale)(datapoint);
    const yCoord = yAreaAccessor(yScale)(datapoint);

    return (
      yCoord >= yMin &&
      yCoord <= yMax &&
      xCoord >= selection.start &&
      xCoord <= selection.end
    );
  });

  return {
    seriesName: name,
    datapoints: filteredData,
    domain: getSelectionDomain(selection, axisConfig),
  };
}

function filterPointSeries<T extends DotDatapoint | LineDatapoint>(
  data: T[],
  xAccessor: (datapoint: T) => number,
  yAccessor: (datapoint: T) => number,
  selection: { start: number; end: number },
  yRange: [number, number],
  isDefined?: ReturnType<typeof isGapDatapointForDisplay<T>>,
): T[] {
  const [yMin, yMax] = yRange;

  return data.filter((datapoint) => {
    if (isDefined && !isDefined(datapoint)) {
      return false;
    }
    const pointX = xAccessor(datapoint);
    const pointY = yAccessor(datapoint);

    return (
      pointX >= selection.start &&
      pointX <= selection.end &&
      pointY >= yMin &&
      pointY <= yMax
    );
  });
}

/**
 * Returns true if the content state refers to a linear (dot, line, or area) point.
 */
export function hasLinearContentPoint(
  contentState: XYChartExploreState['content'],
): boolean {
  return (
    !!contentState?.series &&
    !!contentState?.datapoint &&
    (contentState.type === DOT_VARIANT ||
      contentState.type === LINE_VARIANT ||
      contentState.type === AREA_VARIANT)
  );
}

/**
 * Collect all distinct xAxisIds from provided linear series arrays.
 */
export function collectLinearXAxisIds(
  dotSeries: DotSeriesWithDatapoints[],
  lineSeries: LineSeriesWithDatapoints[],
  areaSeries: AreaSeriesWithDatapoints[],
) {
  const ids = new Set<string>();

  for (const series of dotSeries) {
    if (series.xAxisId) {
      ids.add(series.xAxisId);
    }
  }

  for (const series of lineSeries) {
    if (series.xAxisId) {
      ids.add(series.xAxisId);
    }
  }

  for (const series of areaSeries) {
    if (series.xAxisId) {
      ids.add(series.xAxisId);
    }
  }

  return ids;
}

function getAxisIdToIsRelative(axes: XYChartAxisInternal[]) {
  const axisIdToIsRelative = new Map<string, boolean>();
  axes.forEach((axis) => {
    axisIdToIsRelative.set(axis.id, axis.isRelative);
  });
  return axisIdToIsRelative;
}

/**
 * Build highlighted points derived from a selection (brush / range selection).
 * Returns an empty array if preconditions fail.
 */
export function buildSelectionHighlightedPoints(params: {
  selectState: SelectState | undefined;
  linearSelection: XYChartLinearSelectionContextValue;
  width: number;
  axisIdToScales: AxisIdToScales;
  dotSeriesWithDatapoints: DotSeriesWithDatapoints[];
  lineSeriesWithDatapoints: LineSeriesWithDatapoints[];
  areaSeriesWithDatapoints: AreaSeriesWithDatapoints[];
  dotSeriesMeta: Map<DotSeriesWithDatapoints, DotMetadata>;
  lineSeriesMeta: Map<LineSeriesWithDatapoints, LineMetadata>;
  areaSeriesMeta: Map<AreaSeriesWithDatapoints, AreaMetadata>;
  axisIdToScaleDomain: AxisIdToScaleDomain;
  axes: XYChartAxisInternal[];
  datapointMetadataMap: MapDatapoints;
}): HighlightedLinearPoint[] {
  const {
    selectState,
    linearSelection,
    width,
    axisIdToScales,
    dotSeriesWithDatapoints,
    lineSeriesWithDatapoints,
    areaSeriesWithDatapoints,
    dotSeriesMeta,
    lineSeriesMeta,
    areaSeriesMeta,
    axisIdToScaleDomain,
    axes,
    datapointMetadataMap,
  } = params;

  const anySelection =
    !selectState || linearSelection.selectionConfigByAxisId.size === 0;

  if (anySelection) {
    return [];
  }

  const axisIds = collectLinearXAxisIds(
    dotSeriesWithDatapoints,
    lineSeriesWithDatapoints,
    areaSeriesWithDatapoints,
  );

  const axisIdToIsRelative = getAxisIdToIsRelative(axes);

  if (!axisIds.size) {
    return [];
  }

  const [selectionStart, selectionEnd] = getSelectionPixels(
    selectState.selectionPercentage,
    width,
    [selectState.start, selectState.end],
  );

  const { highlightedDatapoints } = linearSelection.searchAllAxisDatapoints(
    selectionStart.relative.x,
    selectionEnd.relative.x,
    serializeLinearSeriesValues(
      (series: AnyLinearSeries) =>
        getLinearSeriesMetadata(
          series,
          dotSeriesMeta,
          lineSeriesMeta,
          areaSeriesMeta,
        ),
      axisIdToScales,
      axisIdToScaleDomain,
      datapointMetadataMap,
    ),
    Array.from(axisIds),
    axisIdToScales,
    axisIdToScaleDomain,
    axisIdToIsRelative,
  );

  return highlightedDatapoints;
}

/**
 * Build a highlighted point for a dot series.
 */
function buildDotHighlightedPoint(
  datapoint: DotDatapoint,
  meta: DotMetadata,
  axisIdToScales: AxisIdToScales,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): HighlightedLinearPoint[] {
  const xScale = getCurrentScale(axisIdToScales, meta.xAxis.id);
  const yScale = getCurrentScale(axisIdToScales, meta.yAxis.id);

  const { xAxis, yAxis } = meta;
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  return [
    {
      id: DOT_ID,
      x: xDotAccessor(xScale, xRelAccessor)(datapoint) + POINT_OVERLAY_PADDING,
      y: yDotAccessor(yScale, yRelAccessor)(datapoint) + POINT_OVERLAY_PADDING,
      color: meta.color,
      shape: meta.shape,
    },
  ];
}

/**
 * Build a highlighted point for a line series.
 */
function buildLineHighlightedPoint(
  datapoint: LineDatapoint,
  meta: LineMetadata,
  axisIdToScales: AxisIdToScales,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): HighlightedLinearPoint[] {
  const xScale = getCurrentScale(axisIdToScales, meta.xAxis.id);
  const yScale = getCurrentScale(axisIdToScales, meta.yAxis.id);

  const { xAxis, yAxis } = meta;
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  return [
    {
      id: LINE_ID,
      x: xLineAccessor(xScale, xRelAccessor)(datapoint) + POINT_OVERLAY_PADDING,
      y: yLineAccessor(yScale, yRelAccessor)(datapoint) + POINT_OVERLAY_PADDING,
      color: meta.color,
      shape: undefined,
    },
  ];
}

/**
 * Build highlighted points for a horizontal area series.
 */
function buildHorizontalAreaPoints(
  datapoint: AreaDatapoint,
  yScale: AnyScale,
  xScale: AnyScale,
  isBand: boolean,
  color: ColorToken,
  datapointMetadataMap: MapDatapoints,
  xRelAccessor: ((value: number) => number) | undefined,
  yRelAccessor: ((value: number) => number) | undefined,
  isXStacked: boolean,
): HighlightedLinearPoint[] {
  const yCoord = isBand
    ? bandY0Accessor(yScale, yRelAccessor)(datapoint) + POINT_OVERLAY_PADDING
    : yAccessor(yScale, yRelAccessor)(datapoint) + POINT_OVERLAY_PADDING;

  const points: HighlightedLinearPoint[] = [];

  if (isBand) {
    points.push({
      id: AREA_ID,
      x:
        bandX0Accessor(xScale, xRelAccessor)(datapoint) + POINT_OVERLAY_PADDING,
      y: yCoord,
      color,
      shape: undefined,
    });
  }

  points.push({
    id: AREA_ID,
    x:
      (isBand
        ? bandX1Accessor(xScale, xRelAccessor)
        : x1Accessor(xScale, datapointMetadataMap, xRelAccessor, isXStacked))(
        datapoint,
      ) + POINT_OVERLAY_PADDING,
    y: yCoord,
    color,
    shape: undefined,
  });

  return points;
}

/**
 * Build highlighted points for a vertical area series.
 */
function buildVerticalAreaPoints(
  datapoint: AreaDatapoint,
  xScale: AnyScale,
  yScale: AnyScale,
  isBand: boolean,
  color: ColorToken,
  datapointMetadataMap: MapDatapoints,
  xRelAccessor: ((value: number) => number) | undefined,
  yRelAccessor: ((value: number) => number) | undefined,
  isYStacked: boolean,
): HighlightedLinearPoint[] {
  const xCoord = isBand
    ? bandX0Accessor(xScale, xRelAccessor)(datapoint) + POINT_OVERLAY_PADDING
    : xAccessor(xScale, xRelAccessor)(datapoint) + POINT_OVERLAY_PADDING;

  const points: HighlightedLinearPoint[] = [];

  if (isBand) {
    points.push({
      id: AREA_ID,
      x: xCoord,
      y:
        bandY0Accessor(yScale, yRelAccessor)(datapoint) + POINT_OVERLAY_PADDING,
      color,
      shape: undefined,
    });
  }

  points.push({
    id: AREA_ID,
    x: xCoord,
    y:
      (isBand
        ? bandY1Accessor(yScale, yRelAccessor)
        : y1Accessor(yScale, datapointMetadataMap, yRelAccessor, isYStacked))(
        datapoint,
      ) + POINT_OVERLAY_PADDING,
    color,
    shape: undefined,
  });

  return points;
}

/**
 * Build highlighted points for an area series.
 */
function buildAreaHighlightedPoints(
  datapoint: AreaDatapoint,
  meta: AreaMetadata,
  axisIdToScales: AxisIdToScales,
  datapointMetadataMap: MapDatapoints,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): HighlightedLinearPoint[] {
  const isHorizontal = meta.orientation === 'horizontal';
  const isBand = meta.variant === 'band';

  const xScale = getCurrentScale(axisIdToScales, meta.xAxis.id);
  const yScale = getCurrentScale(axisIdToScales, meta.yAxis.id);

  if (!xScale || !yScale) {
    return [];
  }

  const { xAxis, yAxis, isStacked } = meta;

  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  if (isHorizontal) {
    return buildHorizontalAreaPoints(
      datapoint,
      yScale,
      xScale,
      isBand,
      meta.color,
      datapointMetadataMap,
      xRelAccessor,
      yRelAccessor,
      isStacked,
    );
  } else {
    return buildVerticalAreaPoints(
      datapoint,
      xScale,
      yScale,
      isBand,
      meta.color,
      datapointMetadataMap,
      xRelAccessor,
      yRelAccessor,
      isStacked,
    );
  }
}

/**
 * Build highlighted points from the current content (hover/focus) state.
 * Returns an array of points. For area bands, returns two points (top and bottom).
 * Returns an empty array if not linear or scales missing.
 */
export function buildContentHighlightedPoint(params: {
  contentState: XYChartExploreState['content'];
  axisIdToScales: AxisIdToScales;
  dotSeriesMeta: Map<DotSeriesWithDatapoints, DotMetadata>;
  lineSeriesMeta: Map<LineSeriesWithDatapoints, LineMetadata>;
  areaSeriesMeta: Map<AreaSeriesWithDatapoints, AreaMetadata>;
  axisIdToScaleDomain: Map<string, BinDomain>;
  datapointMetadataMap: MapDatapoints;
}): HighlightedLinearPoint[] {
  const {
    contentState,
    axisIdToScales,
    dotSeriesMeta,
    lineSeriesMeta,
    areaSeriesMeta,
    axisIdToScaleDomain,
    datapointMetadataMap,
  } = params;

  if (!hasLinearContentPoint(contentState)) {
    return [];
  }

  if (isDotContentPoint(contentState)) {
    const { series, datapoint } = contentState;
    const meta = safeGet(dotSeriesMeta, series);
    return buildDotHighlightedPoint(
      datapoint,
      meta,
      axisIdToScales,
      axisIdToScaleDomain,
    );
  }

  if (isLineContentPoint(contentState)) {
    const { series, datapoint } = contentState;
    const meta = safeGet(lineSeriesMeta, series);
    return buildLineHighlightedPoint(
      datapoint,
      meta,
      axisIdToScales,
      axisIdToScaleDomain,
    );
  }

  if (isAreaContentPoint(contentState)) {
    const { series, datapoint } = contentState;
    const meta = safeGet(areaSeriesMeta, series);
    return buildAreaHighlightedPoints(
      datapoint,
      meta,
      axisIdToScales,
      datapointMetadataMap,
      axisIdToScaleDomain,
    );
  }

  return [];
}

/**
 * Internal helper reused by serialize function builder.
 */
function getLinearSeriesMetadata(
  series: AnyLinearSeries,
  dotSeriesMeta: Map<DotSeriesWithDatapoints, DotMetadata>,
  lineSeriesMeta: Map<LineSeriesWithDatapoints, LineMetadata>,
  areaSeriesMeta: Map<AreaSeriesWithDatapoints, AreaMetadata>,
): AnyLinearMetadata {
  if (series.type === DOT_VARIANT) {
    return safeGet(dotSeriesMeta, series);
  }
  if (series.type === LINE_VARIANT) {
    return safeGet(lineSeriesMeta, series);
  }

  return safeGet(areaSeriesMeta, series);
}

type SelectStateValue = ReturnType<typeof useSelectState>;
type RectSelectionValue = ReturnType<typeof useXYChartRectSelection>;
type BarSeriesMeta = ReturnType<typeof useBarData>['seriesToMetadata'];
type RectSeriesMeta = ReturnType<typeof useRectData>['seriesToMetadata'];
type RectSeriesList = RectSeriesWithDatapoints[];
type BarSeriesList = BarSeriesWithDatapoints[];

export function buildSelectionHighlightedRectangles(params: {
  selectState: SelectStateValue;
  rectSelection: RectSelectionValue;
  width: number;
  rectSeriesMeta: Map<RectSeriesWithDatapoints, RectMetadata>;
  rectSeriesWithDatapoints: RectSeriesList;
  barSeriesWithDatapoints: BarSeriesList;
  barSeriesMeta: BarSeriesMeta;
  axisIdToScales: ReturnType<typeof useXYChartScales>;
  axisIdToScaleDomain: AxisIdToScaleDomain;
  axes: XYChartAxisInternal[];
  datapointMetadataMap: MapDatapoints;
}): RectanglePoint[] {
  const {
    selectState,
    rectSelection,
    width,
    rectSeriesMeta,
    rectSeriesWithDatapoints,
    barSeriesWithDatapoints,
    barSeriesMeta,
    axisIdToScales,
    axisIdToScaleDomain,
    axes,
    datapointMetadataMap,
  } = params;

  if (!selectState || rectSelection.selectionConfigByAxisId.size === 0) {
    return [];
  }

  const definedAxes = Array.from(
    new Set(
      [...rectSeriesWithDatapoints, ...barSeriesWithDatapoints].flatMap(
        (series) => {
          const axes: string[] = [];
          if (!isUndefined(series.xAxisId)) {
            axes.push(series.xAxisId);
          }
          if (!isUndefined(series.yAxisId)) {
            axes.push(series.yAxisId);
          }
          return axes;
        },
      ),
    ),
  );

  const axisIdToIsRelative = getAxisIdToIsRelative(axes);

  if (!definedAxes.length) {
    return [];
  }

  const [selectionStart, selectionEnd] = getSelectionPixels(
    selectState.selectionPercentage,
    width,
    [selectState.start, selectState.end],
  );

  const { highlightedDatapoints } = rectSelection.searchAllAxisDatapoints(
    selectionStart.relative.x,
    selectionEnd.relative.x,
    serializeRectangleSeriesValues(
      axisIdToScales,
      rectSeriesMeta,
      axisIdToScaleDomain,
      datapointMetadataMap,
      barSeriesMeta,
    ),
    definedAxes,
    axisIdToScales,
    axisIdToScaleDomain,
    axisIdToIsRelative,
  );

  return highlightedDatapoints;
}

export function buildContentHighlightedRectangle(params: {
  contentState: XYChartExploreState['content'];
  axisIdToScales: ReturnType<typeof useXYChartScales>;
  barSeriesMeta: BarSeriesMeta;
  rectSeriesMeta: RectSeriesMeta;
  datapointMetadataMap: MapDatapoints;
  axisIdToScaleDomain: Map<string, BinDomain>;
}): RectanglePoint | null {
  const {
    contentState,
    axisIdToScales,
    barSeriesMeta,
    rectSeriesMeta,
    datapointMetadataMap,
    axisIdToScaleDomain,
  } = params;

  if (
    !contentState?.series ||
    !contentState?.datapoint ||
    (contentState.type !== BAR_VARIANT && contentState.type !== RECT_VARIANT)
  ) {
    return null;
  }

  if (contentState.type === BAR_VARIANT) {
    const { datapoint, series } = contentState;
    const { xAxis, yAxis } = safeGet(barSeriesMeta, series);
    const xScale = getCurrentScale(
      axisIdToScales,
      xAxis.id,
      isXBaselineAccessor(series.data[0]) ? series.type : undefined,
    );
    const dimensionXScale = getDimensionScale(axisIdToScales, series.xAxisId);
    const yScale = getCurrentScale(
      axisIdToScales,
      yAxis.id,
      isYBaselineAccessor(series.data[0]) ? series.type : undefined,
    );
    const dimensionYScale = getDimensionScale(axisIdToScales, series.yAxisId);
    const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
    const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

    return {
      id: BAR_ID,
      x: xBarAccessor(
        xScale,
        datapointMetadataMap,
        dimensionXScale,
        series.id,
        xRelAccessor,
      )(datapoint),
      y: yBarAccessor(
        yScale,
        datapointMetadataMap,
        dimensionYScale,
        series.id,
        yRelAccessor,
      )(datapoint),
      width: barWidthAccessor(
        xScale,
        datapointMetadataMap,
        dimensionXScale,
        xRelAccessor,
      )(datapoint),
      height: barHeightAccessor(
        yScale,
        datapointMetadataMap,
        dimensionYScale,
        yRelAccessor,
      )(datapoint),
    };
  }

  const { datapoint, series } = contentState;
  const { xAxis, yAxis } = safeGet(rectSeriesMeta, series);
  const xScale = getCurrentScale(axisIdToScales, xAxis.id);
  const yScale = getCurrentScale(axisIdToScales, yAxis.id);
  const xRelAccessor = relAccessor(xAxis, axisIdToScaleDomain);
  const yRelAccessor = relAccessor(yAxis, axisIdToScaleDomain);

  return {
    id: RECT_ID,
    x: xRectAccessor(xScale, xRelAccessor)(datapoint),
    y: yRectAccessor(yScale, yRelAccessor)(datapoint),
    width: widthAccessor(xScale, xRelAccessor)(datapoint),
    height: heightAccessor(yScale, yRelAccessor)(datapoint),
  };
}

export function buildRectangleHighlightedPoints(params: {
  selectState: SelectStateValue;
  rectSelection: RectSelectionValue;
  width: number;
  rectSeriesWithDatapoints: RectSeriesList;
  barSeriesWithDatapoints: BarSeriesList;
  axisIdToScales: ReturnType<typeof useXYChartScales>;
  barSeriesMeta: BarSeriesMeta;
  rectSeriesMeta: RectSeriesMeta;
  datapointMetadataMap: MapDatapoints;
  axisIdToScaleDomain: Map<string, BinDomain>;
  axes: XYChartAxisInternal[];
  allRectangleDatapoints: TooltipContent[];
}): RectanglePoint[] {
  const selectionRects = buildSelectionHighlightedRectangles(params);

  const contentRects = params.allRectangleDatapoints.flatMap((contentState) => {
    const rect = buildContentHighlightedRectangle({
      ...params,
      contentState,
    });
    return rect ? [rect] : [];
  });
  return [...selectionRects, ...contentRects];
}
